import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { type FullBackupData, type MeasuresData, type MeasureBackupRequirements, KeyMeasureSchema } from '@/lib/backup/measure-backup';
import { useMeasureActions } from '@/hooks/store/use-measure-store';
import { useDesignActions } from '@/hooks/store/use-design-store';
import { useMeasureOptionsActions } from '@/hooks/store/use-measure-options-store';
import { usePianoProfileActions } from '@/hooks/store/use-piano-profile-store';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import type { Note } from '@/lib/music/theory/spn';
import type z from 'zod';
const defaultKeySpec = {
  downWeightWithSpringSupport: null,
  downWeightWithoutSpringSupport: null,
  frontWeight: null,
  keyWeightRatio: null,
  measuredStrikeWeightRatio: null,
  strikeWeight: null,
  upWeight: null,
  wippenRadiusWeight: null
};
export const useImportBackupData = () => {
  const $ = _c(17);
  const {
    updateState: updateMeasureState
  } = useMeasureActions();
  const {
    updateFrontWeightDesign,
    updateStrikeWeightDesign,
    updateStrikeWeightRatioDesign,
    updateWippenSupportSpringsDesign
  } = useDesignActions();
  const {
    updateState: updateMeasureOptionsState
  } = useMeasureOptionsActions();
  const {
    updateState: updatePianoState
  } = usePianoProfileActions();
  const {
    keyboard
  } = useKeyboard();
  const transformKeys = _temp2;
  let t0;
  if ($[0] !== keyboard.size) {
    t0 = keys_0 => {
      if (keys_0.length >= keyboard.size) {
        return keys_0;
      }
      const extendedKeys = [...keys_0];
      for (let i = keys_0.length; i < keyboard.size; i++) {
        extendedKeys.push({
          ...defaultKeySpec
        });
      }
      return extendedKeys;
    };
    $[0] = keyboard.size;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const extendKeysToKeyboardSize = t0;
  let t1;
  if ($[2] !== extendKeysToKeyboardSize || $[3] !== updateMeasureState) {
    t1 = data => {
      const nextKeys = transformKeys(data.keys);
      const extendedKeys_0 = extendKeysToKeyboardSize(nextKeys);
      updateMeasureState({
        keyWeightRatio: data.keyWeightRatio ?? null,
        keys: extendedKeys_0,
        wippenRadiusWeight: data.wippenRadiusWeight ?? null
      });
    };
    $[2] = extendKeysToKeyboardSize;
    $[3] = updateMeasureState;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const importMeasuresOnly = t1;
  let t2;
  if ($[5] !== extendKeysToKeyboardSize || $[6] !== updateFrontWeightDesign || $[7] !== updateMeasureOptionsState || $[8] !== updateMeasureState || $[9] !== updatePianoState || $[10] !== updateStrikeWeightDesign || $[11] !== updateStrikeWeightRatioDesign || $[12] !== updateWippenSupportSpringsDesign) {
    t2 = data_0 => {
      const nextKeys_0 = transformKeys(data_0.measures.keys);
      const extendedKeys_1 = extendKeysToKeyboardSize(nextKeys_0);
      updateMeasureState({
        keyWeightRatio: data_0.measures.keyWeightRatio ?? null,
        keys: extendedKeys_1,
        wippenRadiusWeight: data_0.measures.wippenRadiusWeight ?? null
      });
      updateFrontWeightDesign(data_0.design.frontWeightDesignMode as never, data_0.design.frontWeightDesignTarget);
      updateStrikeWeightDesign(data_0.design.strikeWeightDesignMode as never, data_0.design.strikeWeightDesignTarget as never);
      updateStrikeWeightRatioDesign(data_0.design.strikeWeightRatioDesignMode as never, data_0.design.strikeWeightRatioDesignTarget);
      updateWippenSupportSpringsDesign(data_0.design.wippenSupportSpringsDesignMode as never, data_0.design.wippenSupportSpringsDesignTarget as never);
      updateMeasureOptionsState({
        useManualSWRMeasurements: data_0.measureOptions.useManualSWRMeasurements,
        useSupportSpringMeasurements: data_0.measureOptions.useSupportSpringMeasurements
      });
      if (data_0.piano) {
        updatePianoState({
          brand: data_0.piano.brand ?? null,
          isDemoProfile: data_0.piano.isDemoProfile ?? false,
          keyCount: data_0.piano.keyCount ?? 88,
          model: data_0.piano.model ?? null,
          serialNumber: data_0.piano.serialNumber ?? null,
          startNote: (data_0.piano.startNote ?? {
            letter: "A",
            octave: 0
          }) as Note
        });
      }
    };
    $[5] = extendKeysToKeyboardSize;
    $[6] = updateFrontWeightDesign;
    $[7] = updateMeasureOptionsState;
    $[8] = updateMeasureState;
    $[9] = updatePianoState;
    $[10] = updateStrikeWeightDesign;
    $[11] = updateStrikeWeightRatioDesign;
    $[12] = updateWippenSupportSpringsDesign;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const importFullBackup = t2;
  let t3;
  if ($[14] !== importFullBackup || $[15] !== importMeasuresOnly) {
    t3 = {
      importFullBackup,
      importMeasuresOnly
    };
    $[14] = importFullBackup;
    $[15] = importMeasuresOnly;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
function _temp(k) {
  return {
    downWeightWithSpringSupport: k.downWeightWithSpringSupport ?? null,
    downWeightWithoutSpringSupport: k.downWeightWithoutSpringSupport ?? k.downWeight ?? null,
    frontWeight: k.frontWeight ?? null,
    keyWeightRatio: k.keyWeightRatio ?? null,
    measuredStrikeWeightRatio: k.measuredStrikeWeightRatio ?? null,
    strikeWeight: k.strikeWeight ?? null,
    upWeight: k.upWeight ?? null,
    wippenRadiusWeight: k.wippenRadiusWeight ?? null
  };
}
function _temp2(keys) {
  return keys.map(_temp);
}